import { push } from 'react-router-redux';
import { joinPath } from '@eventbrite/path-utils';
import { setWindowLocation } from '@eventbrite/http';
import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eventbrite/site-analytics';
import { setAssortment as setAssortmentApi } from '../api';

import {
    GA_CHANGE_PLAN_CATEGORY,
    GA_CHOOSE_PACKAGE,
    GA_CONFLICTS_DOWNGRADE_CLICK,
    GA_CONFLICTS_DOWNGRADE_KEEP_PACKAGE,
    GA_CONTACT_CHANGE_PLAN_CLICK,
    GA_DOWNGRADE_CHANGE_PLAN_CLICK,
    GA_DOWNGRADE_KEEP_PACKAGE,
    GA_P1_LABEL,
    GA_P1_V2_LABEL,
    GA_P2_LABEL,
    GA_PACKAGE_DOWNGRADED,
    GA_PACKAGE_UPDATED,
    GA_PACKAGE_UPGRADED,
    GA_PACKAGE_VIEWED,
    GA_UPDATE_CHANGE_PLAN_VIEW,
    GA_UPDATE_PAGE_CLICK,
    GA_UPGRADE_CHANGE_PLAN_CLICK,
} from '../constants/analytics';
import {
    ACCOUNT_SETTINGS_DOWNGRADE_FAIL_PATH,
    ACCOUNT_SETTINGS_DOWNGRADE_SUCCESS_PATH,
    ACCOUNT_SETTINGS_UPGRADE_FAIL_PATH,
    ACCOUNT_SETTINGS_UPGRADE_SUCCESS_PATH,
    ACCOUNT_SETTINGS_URL,
    BASE_URL,
    CHANGE_PLAN_DOWNGRADE_PATH,
    CHANGE_PLAN_UPDATE_PATH,
    CONTACT_SALES_URL,
    CONTACT_US_PATH,
    PACKAGE_ONE,
    PACKAGE_THREE,
    PACKAGE_TWO,
    PACKAGE_VERSION_2,
} from '../constants/main';
import { handleRedirect } from './utils';
import { isActiveForAU, trackAnalyticsEventAU } from '../containers/utils';

const _handleServerError = ({ parsedError: { error } }, redirectUrl) => {
    setWindowLocation(`${redirectUrl}&errorType=${error}`);
};

const _setPackage =
    (
        plan,
        activeScopeUserId,
        eventId,
        redirectSuccessUrl,
        redirectFailureUrl,
        version,
    ) =>
    () => {
        setAssortmentApi({ plan, version, activeScopeUserId, eventId }).then(
            () => setWindowLocation(redirectSuccessUrl),
            ({ extraData }) =>
                _handleServerError(extraData, redirectFailureUrl),
        );
    };

export const _trackEventActionsAU = (action) => (dispatch, getState) => {
    const {
        env: { locale },
        user: { userId, userPlan },
        assortments: {
            assortmentsAvailability: {
                current: { version },
            },
        },
    } = getState();
    if (isActiveForAU(locale)) {
        trackAnalyticsEventAU({
            action: action,
            category: GA_CHOOSE_PACKAGE,
            dimensions: {
                userId,
                userPlan,
                version,
                locale,
            },
        });
    }
};

const _getRedirectSuccessUrl = (referrer, domain, defaultUrl) => {
    if (referrer) {
        // Ensure that only local urls are allowed
        return `https://www.${domain}${referrer}`;
    }

    return defaultUrl;
};

export const goToDowngrade = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { search },
        },
    } = state;
    const location = {
        pathname: joinPath(BASE_URL, CHANGE_PLAN_DOWNGRADE_PATH),
        search,
    };

    trackEventFromState(state, {
        action: GA_DOWNGRADE_CHANGE_PLAN_CLICK,
        label: GA_P2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_DOWNGRADED));
    dispatch(push(location));
};

export const goToUpdateVersionTwo = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { search },
        },
    } = state;
    const location = {
        pathname: joinPath(BASE_URL, CHANGE_PLAN_UPDATE_PATH),
        search,
    };

    trackEventFromState(state, {
        action: GA_UPDATE_PAGE_CLICK,
        label: GA_P1_V2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_UPDATED));
    dispatch(_trackEventActionsAU(GA_PACKAGE_VIEWED));
    dispatch(push(location));
};

export const setPackageOneVersionTwo = () => (dispatch, getState) => {
    const state = getState();
    const {
        user: { activeScopeUserId },
        routing: {
            locationBeforeTransitions: {
                query: { referrer, eventId },
            },
        },
        env: { domain },
    } = state;

    const action = _setPackage(
        PACKAGE_ONE,
        activeScopeUserId,
        eventId,
        _getRedirectSuccessUrl(
            referrer,
            domain,
            ACCOUNT_SETTINGS_UPGRADE_SUCCESS_PATH,
        ),
        ACCOUNT_SETTINGS_UPGRADE_FAIL_PATH,
        PACKAGE_VERSION_2,
    );

    trackEventFromState(state, {
        action: GA_UPDATE_CHANGE_PLAN_VIEW,
        label: GA_P2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_UPDATED));
    dispatch(action);
};

export const setPackageOne = () => (dispatch, getState) => {
    const state = getState();
    const {
        user: { activeScopeUserId },
        routing: {
            locationBeforeTransitions: {
                query: { referrer, eventId },
            },
        },
        env: { domain },
    } = state;

    const action = _setPackage(
        PACKAGE_ONE,
        activeScopeUserId,
        eventId,
        _getRedirectSuccessUrl(
            referrer,
            domain,
            ACCOUNT_SETTINGS_DOWNGRADE_SUCCESS_PATH,
        ),
        ACCOUNT_SETTINGS_DOWNGRADE_FAIL_PATH,
    );

    dispatch(action);
};

export const setPackageTwo = () => (dispatch, getState) => {
    const state = getState();
    const {
        user: { activeScopeUserId },
        routing: {
            locationBeforeTransitions: {
                query: { referrer, eventId },
            },
        },
        env: { domain },
    } = state;
    const action = _setPackage(
        PACKAGE_TWO,
        activeScopeUserId,
        eventId,
        _getRedirectSuccessUrl(
            referrer,
            domain,
            ACCOUNT_SETTINGS_UPGRADE_SUCCESS_PATH,
        ),
        ACCOUNT_SETTINGS_UPGRADE_FAIL_PATH,
    );

    trackEventFromState(state, {
        action: GA_UPGRADE_CHANGE_PLAN_CLICK,
        label: GA_P1_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_UPGRADED));
    dispatch(action);
};

export const onKeepPackage = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: {
                query: { referrer },
            },
        },
        env: { domain },
    } = state;
    const url = _getRedirectSuccessUrl(referrer, domain, ACCOUNT_SETTINGS_URL);

    trackEventFromStateOnPageUnload(state, {
        action: GA_DOWNGRADE_KEEP_PACKAGE,
        label: GA_P2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });

    dispatch(handleRedirect(url));
};

export const onKeepPackageAfterConflicts = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: {
                query: { referrer },
            },
        },
        env: { domain },
    } = state;
    const url = _getRedirectSuccessUrl(referrer, domain, ACCOUNT_SETTINGS_URL);

    trackEventFromStateOnPageUnload(state, {
        action: GA_CONFLICTS_DOWNGRADE_KEEP_PACKAGE,
        label: GA_P2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_DOWNGRADED));

    dispatch(handleRedirect(url));
};

export const onChangePackageAfterConflicts = () => (dispatch, getState) => {
    const state = getState();
    trackEventFromStateOnPageUnload(state, {
        action: GA_CONFLICTS_DOWNGRADE_CLICK,
        label: GA_P2_LABEL,
        category: GA_CHANGE_PLAN_CATEGORY,
    });
    dispatch(_trackEventActionsAU(GA_PACKAGE_DOWNGRADED));
    dispatch(setPackageOne());
};

export const contactUsRedirect = () => (dispatch, getState) => {
    const state = getState();
    const {
        user: { userPlan },
    } = state;
    let packageLabel = GA_P1_LABEL;
    let url = CONTACT_SALES_URL;

    if (userPlan === PACKAGE_TWO) {
        packageLabel = GA_P2_LABEL;
    }

    if (userPlan === PACKAGE_THREE) {
        url = CONTACT_US_PATH;
    }

    trackEventFromStateOnPageUnload(state, {
        action: GA_CONTACT_CHANGE_PLAN_CLICK,
        label: packageLabel,
        category: GA_CHANGE_PLAN_CATEGORY,
    });

    dispatch(handleRedirect(url));
};
